import { useTranslation } from "react-i18next";
import { ColorV2 } from "config/colors";
import { GigInfoContainer } from "./clockStamp.styled";
import moment from "moment-timezone";
import { TimeReportForStamping } from "model/Company";
import { useCallback } from "react";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import styled from "styled-components";

type Props = {
  timeReport?: TimeReportForStamping;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setKeyShouldBeDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  isAutoGeneratedGig: boolean;
  setIsAutoGeneratedGig: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmitTimeReport: () => void;
  clearInputs: () => void;
};

export const ClockStampCheckInOutModal: React.FC<Props> = ({
  timeReport,
  setShowModal,
  setKeyShouldBeDisabled,
  showModal,
  isAutoGeneratedGig,
  setIsAutoGeneratedGig,
  handleSubmitTimeReport,
  clearInputs,
}) => {
  const { t } = useTranslation();

  const isCheckOut =
    timeReport?.checkInTime && !isAutoGeneratedGig && !timeReport?.checkOutTime;
  const userName = (timeReport?.firstName || "").trim();
  const welcomeText = userName
    ? `${t("StampClock.Welcome")} ${userName}!`
    : `${t("StampClock.Welcome")}!`;
  const haveANiceDayText = userName
    ? `${t("StampClock.HaveANiceDay")} ${userName}!`
    : `${t("StampClock.HaveANiceDay")}!`;

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setKeyShouldBeDisabled(false);
    setIsAutoGeneratedGig(false);
    clearInputs();
  }, [
    setShowModal,
    setKeyShouldBeDisabled,
    setIsAutoGeneratedGig,
    clearInputs,
  ]);

  const formatTime = useCallback((time?: string | null) => {
    if (!time) return "—";
    return moment
      .tz(time, "UTC")
      .tz("Europe/Stockholm")
      .format("YYYY-MM-DD HH:mm");
  }, []);

  return (
    <Modal
      label=""
      size="compact"
      isOpen={showModal}
      onClose={handleCloseModal}
    >
      <ModalContent>
        <Text typography="h3" align="center" color={ColorV2.Blue1}>
          {isCheckOut ? haveANiceDayText : welcomeText}
        </Text>
        <Text typography="b1" align="center" color={ColorV2.Blue1}>
          {isCheckOut
            ? t("StampClock.YouAreCheckedOut")
            : t("StampClock.YouAreCheckedIn")}
        </Text>
        {!isCheckOut && timeReport && (
          <GigInfoContainer>
            <Text typography="b2" align="left" color={ColorV2.Blue1}>
              {timeReport.gigRole}
            </Text>
            <Text typography="b2" align="left" color={ColorV2.Blue1}>
              {`${t("StampClock.StartTime")}: ${formatTime(
                timeReport.startTime,
              )}`}
            </Text>
            <Text typography="b2" align="left" color={ColorV2.Blue1}>
              {`${t("StampClock.EndTime")}: ${formatTime(timeReport.endTime)}`}
            </Text>
          </GigInfoContainer>
        )}
        {isCheckOut && (
          <Actions>
            <PrimaryButton
              label={t("CompanyTimeReport.IWantToTimeReport")}
              onClick={handleSubmitTimeReport}
              size="small"
            />
          </Actions>
        )}
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 14px;
`;
